<script>
import { curves } from '../../../utils'

const radius = 110

export default {
  name: "Snippet",
  props: [
    "snippet",
    "selectedAudio",
    "inlayDimensions",
    "zoom",
    "elapsed",
    "isSelected",
    "isVisible",
  ],
  emits: [
    'selectAudio'
  ],
  data() {
    return {
      radius: radius,
      offset: Math.random() * 100,
      trackShape: {
        viewBox: `0 0 ${radius*2} ${radius*2}`,
        path: curves.generateCurvyShape({
          vertixCountFactor: 0.55,
          centerX: radius,
          centerY: radius,
          radius: radius,
        }),
      }
    }
  },
  computed: {
    anchor()      { return this.snippet.anchor?.slug },
    slug()        { return this.snippet.slug },
    keyword()     { return this.snippet.Keyword },
    audios()      { return this.snippet.audios },
    x()           { return this.snippet.x * this.inlayDimensions.w + this.inlayDimensions.x },
    y()           { return this.snippet.y * this.inlayDimensions.h + this.inlayDimensions.y },
    waveData()    { return (
      this.isSelected && 
      this.audios.length > 0 &&
      this.audios.indexOf(this.selectedAudio) > -1 &&
      this.normalizeData(this.selectedAudio.waveData) 
    )},
    currentWave() { return (
      this.waveData ? (
        this.waveData[Math.floor(
          this.waveData.length * this.elapsed
        )] < 0.05 && 0.05 * 2.5
      ) || (
        this.waveData[Math.floor(
          this.waveData.length * this.elapsed
        ) + 1 ] * 2.5
      ) : 0.1
    ) },
    computedZoom(){ return this.zoom * this.currentWave },
  },
  methods: {
    normalizeData: data => data.map(n => n * Math.pow(Math.max(...data), -0.4)),
  },
}
</script>

<template>
  <div 
    :class="[
      'snippet',
      { isVisible }
    ]"
    :style="{ 
      '--radius' : radius,
      '--offset' : offset,
      '--top'    : y,
      '--left'   : x,
    }"
  >
    <div class="speakers">
      <div 
        v-for="audio in audios"
        :key="audio.title"
        :class="[
          'speaker',
          'ui',
          { hover: isSelected && audio == selectedAudio }
        ]"
        :style="{ 
          '--zoom' : (audio == selectedAudio) && computedZoom , 
        }"
        aria-role="button"
        :aria-label="'This is an audio snippet belonging to the ' + anchor + ' anchor on the map. Press enter to listen to it.'"
        :tabindex="isVisible ? 0 : -1"
        @click="$emit('selectAudio', audio)"
        @keydown.enter="$emit('selectAudio', audio)"
        @keydown.space="$emit('selectAudio', audio)"
      ></div>
    </div>

    <svg 
      class="text"
      :viewBox="trackShape.viewBox"
      preserveAspectRatio="none"
    >
      <path 
        class="shape"
        ref="shape"
        :id="`s-${slug}`"
        :d="trackShape.path" 
      />
      <text text-anchor="start">
        <textPath 
          :xlink:href="`#s-${slug}`"
          :startOffset="offset + '%'"
        >
        <animate 
            v-if="isSelected"
            attributeName="startOffset" 
            :from="100 + '%'" 
            :to="-0 + '%'" 
            begin="0s" 
            dur="30s" 
            repeatCount="indefinite"
          ></animate>
          {{  keyword }}
        </textPath>
      </text>
    </svg>
  </div>
</template>

<style scoped>
.snippet {
  position: absolute;
  transition: all var(--slow) ease;
  transform-origin: center center;
  opacity: 0;
  transform: scale(0.8);
  top: calc(var(--top) * 1px);
  left:  calc(var(--left) * 1px);
  cursor: grab;
  z-index: 1;
  pointer-events: none;
}
.snippet.isVisible {
  opacity: 1;
  transform: scale(1); 
  cursor: pointer;
  pointer-events: all;
}
.snippet .speakers  {
  position: absolute;
  top: -1em; left: -1em;
}
.snippet .speaker.ui {
  position: relative;
  padding: 0.5em;
  width: 10%; height: 10%;
  transform: scale(var(--zoom));
  transition: 
    filter var(--veryslow) ease,
    transform var(--slow) ease,
  ;
}
.snippet .speaker.ui:nth-of-type(2) {
  margin-left: 1em;
}
.snippet svg {
  /* position: relative; */
  position: absolute;  
  min-width: calc(var(--radius) * 1px);
  min-height: calc(var(--radius) * 1px);
  top: calc(var(--radius)/2 * -1px);
  left: calc(var(--radius)/2 * -1px);
  fill: none;
  overflow: visible;
  background: none;
  pointer-events: none;
}
.snippet svg.text {
  transition: all var(--slow) ease;
}
.physical .snippet svg.text {
  opacity: 0;
}
.snippet svg.text path {
  position: absolute;  
  width: calc(var(--radius) * 1px);
  height: calc(var(--radius) * 1px);
  top: calc(var(--radius)/2 * -1px);
  left: calc(var(--radius)/2 * -1px);

  /* stroke: var(--light2);
  stroke-width: 2px; */
}
.snippet svg.text text {
  position: relative;
  fill: white;
  font-size: 1.8em;
}


.routing .ui,
.ui:focus,
.ui:focus-within,
.ui.hover,
.ui:hover {
  filter: drop-shadow(0 0 80px var(--light1));
  transform: scale(0.9);
  transform-origin: center center;
  transition: 
    filter var(--veryslow) ease,
    transform var(--slow) ease,
  ;
}
.snippet:focus .ui::before,
.snippet:focus .ui::after {
  filter: blur(13px);
}
.snippet:focus .ui::before {
  transform: scale(0.8);
  opacity: 0.9;
  background: var(--light1);
  box-shadow: 0 0 30px 12px var(--light1);
  transition: 
    filter var(--veryslow) ease,
    transform var(--veryslow) ease,
    opacity var(--veryslow) ease,
    background var(--veryslow) ease,
    box-shadow var(--veryslow) ease
  ;
}
.snippet:focus .ui::after {
  transform: scale(0.75);
  opacity: 0.95;
  background: var(--light2);
  box-shadow: 0 0 22px 10px var(--light2);
  transition: 
    filter var(--slow) ease,
    transform var(--slow) ease,
    opacity var(--slow) ease,
    background var(--slow) ease,
    box-shadow var(--slow) ease
  ;
}

</style>
